/**
 * IBAN validation utility for frontend
 * Validates IBAN numbers according to ISO 13616
 */

// Country-specific IBAN lengths
const IBAN_LENGTHS: Record<string, number> = {
  AL: 28, AD: 24, AT: 20, AZ: 28, BH: 22, BY: 28, BE: 16, BA: 20,
  BR: 29, BG: 22, CR: 22, HR: 21, CY: 28, CZ: 24, DK: 18, DO: 28,
  EG: 29, SV: 28, EE: 20, FO: 18, FI: 18, FR: 27, GE: 22, DE: 22,
  GI: 23, GR: 27, GL: 18, GT: 28, HU: 28, IS: 26, IQ: 23, IE: 22,
  IL: 23, IT: 27, JO: 30, KZ: 20, XK: 20, KW: 30, LV: 21, LB: 28,
  LY: 25, LI: 21, LT: 20, LU: 20, MK: 19, MT: 31, MR: 27, MU: 30,
  MC: 27, MD: 24, ME: 22, NL: 18, NO: 15, PK: 24, PS: 29, PL: 28,
  PT: 25, QA: 29, RO: 24, LC: 32, SM: 27, ST: 25, SA: 24, RS: 22,
  SC: 31, SK: 24, SI: 19, ES: 24, SD: 18, SE: 24, CH: 21, TL: 23,
  TN: 24, TR: 26, UA: 29, AE: 23, GB: 22, VA: 22, VG: 24,
};

/**
 * Clean IBAN - remove spaces and convert to uppercase
 */
export function cleanIban(iban: string): string {
  return iban.replace(/\s/g, '').toUpperCase();
}

/**
 * Format IBAN with spaces every 4 characters
 */
export function formatIban(iban: string): string {
  const cleaned = cleanIban(iban);
  return cleaned.replace(/(.{4})/g, '$1 ').trim();
}

/**
 * Validate IBAN checksum using MOD-97 algorithm
 */
function validateChecksum(iban: string): boolean {
  // Move first 4 chars to end
  const rearranged = iban.slice(4) + iban.slice(0, 4);

  // Convert letters to numbers (A=10, B=11, etc.)
  let numericString = '';
  for (const char of rearranged) {
    if (char >= 'A' && char <= 'Z') {
      numericString += (char.charCodeAt(0) - 55).toString();
    } else {
      numericString += char;
    }
  }

  // Calculate MOD 97 in chunks to handle big numbers
  let remainder = 0;
  for (let i = 0; i < numericString.length; i += 7) {
    const chunk = remainder.toString() + numericString.slice(i, i + 7);
    remainder = parseInt(chunk, 10) % 97;
  }

  return remainder === 1;
}

/**
 * Validate IBAN format and checksum
 */
export function validateIban(iban: string): { valid: boolean; error?: string } {
  if (!iban) {
    return { valid: false, error: 'IBAN is required' };
  }

  const cleaned = cleanIban(iban);

  // Check minimum length
  if (cleaned.length < 15) {
    return { valid: false, error: 'IBAN is too short' };
  }

  // Check maximum length
  if (cleaned.length > 34) {
    return { valid: false, error: 'IBAN is too long' };
  }

  // Extract country code
  const countryCode = cleaned.slice(0, 2);

  // Check country code format (2 letters)
  if (!/^[A-Z]{2}$/.test(countryCode)) {
    return { valid: false, error: 'Invalid country code' };
  }

  // Check if country is known and validate length
  const expectedLength = IBAN_LENGTHS[countryCode];
  if (expectedLength && cleaned.length !== expectedLength) {
    return {
      valid: false,
      error: `Invalid IBAN length for ${countryCode}. Expected ${expectedLength} characters`,
    };
  }

  // Check check digits (positions 3-4 must be digits)
  if (!/^\d{2}$/.test(cleaned.slice(2, 4))) {
    return { valid: false, error: 'Invalid check digits' };
  }

  // Check remaining characters (alphanumeric)
  if (!/^[A-Z0-9]+$/.test(cleaned.slice(4))) {
    return { valid: false, error: 'Invalid characters in IBAN' };
  }

  // Validate checksum
  if (!validateChecksum(cleaned)) {
    return { valid: false, error: 'Invalid IBAN checksum' };
  }

  return { valid: true };
}

/**
 * Extract country code from IBAN
 */
export function getIbanCountry(iban: string): string {
  return cleanIban(iban).slice(0, 2);
}

// Re-export from shared-types for convenience
import { SEPA_COUNTRIES, type PaymentType } from '@ip/shared-types';
export { SEPA_COUNTRIES };

const BIC_REGEX = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

/**
 * Check if a country code is in the SEPA zone
 */
export function isSepaCountry(countryCode: string): boolean {
  return SEPA_COUNTRIES.has(countryCode.toUpperCase());
}

/**
 * Detect payment type from creditor IBAN and currency
 */
export function detectPaymentType(creditorIban: string, currency: string): PaymentType {
  const cleaned = cleanIban(creditorIban);
  const country = cleaned.slice(0, 2);
  const cur = (currency || 'PLN').toUpperCase();

  if (country === 'PL' && cur === 'PLN') return 'domestic';
  if (cur === 'EUR' && isSepaCountry(country)) return 'sepa';
  return 'swift';
}

/**
 * Detect payment type from account number (IBAN or non-IBAN) + currency + vendor country.
 * Single source of truth for both InvoiceDetail and MBankExportView.
 * Returns null type when non-IBAN and country is unknown/PL (can't determine type).
 */
export function detectPaymentTypeFromAccount(
  account: string,
  currency: string,
  vendorCountry?: string | null,
): { type: PaymentType | null; isIban: boolean } {
  const cleaned = cleanIban(account);
  const validation = validateIban(cleaned);

  if (validation.valid) {
    return { type: detectPaymentType(cleaned, currency), isIban: true };
  }

  // Non-IBAN account — infer from vendor country
  if (vendorCountry && vendorCountry !== 'PL') {
    // Max34Text XSD constraint — account exceeding 34 chars cannot be exported
    if (cleaned.length > 34) {
      return { type: null, isIban: false };
    }
    return { type: 'swift', isIban: false };
  }

  // Can't determine — return null type (shows "Unknown" badge)
  return { type: null, isIban: false };
}

/**
 * Validate SWIFT/BIC code format (ISO 9362)
 */
export function validateSwiftBic(bic: string): { valid: boolean; error?: string } {
  if (!bic) return { valid: false, error: 'SWIFT/BIC is required' };
  const cleaned = bic.toUpperCase().trim();
  if (cleaned.length !== 8 && cleaned.length !== 11) {
    return { valid: false, error: 'SWIFT/BIC must be 8 or 11 characters' };
  }
  if (!BIC_REGEX.test(cleaned)) {
    return { valid: false, error: 'Invalid SWIFT/BIC format' };
  }
  return { valid: true };
}
